import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, Text, cn } from '@kognitos/lattice';
import {
  CATALOG_ACCENT,
  type TCatalogAccent,
} from '@/modules/process-catalog/workstream-accent';
import { SPRING_EASE_CLASS } from '@/shared/constants/motion';
import { getInitials } from '@/shared/utils';

import type { ITemplateCard } from '../../constants';

/**
 * One accent per team, so a team's people read as a team at a glance. Finance
 * teams match the colours their workstreams already use elsewhere.
 */
export const TEAM_ACCENT: Record<string, TCatalogAccent> = {
  controllership: 'violet',
  procureToPay: 'blue',
  orderToCash: 'mint',
  treasury: 'cyan',
  fpa: 'rose',
  tax: 'amber',
  payroll: 'olive',
  retailDeposits: 'blue',
  lending: 'mint',
  corporateBanking: 'violet',
  payments: 'cyan',
  cards: 'rose',
  relationshipSales: 'amber',
  channelsService: 'olive',
  wealth: 'cyan',
  product: 'violet',
};

const SIZES = {
  sm: { avatar: 'size-7', text: 'xSmall' },
  md: { avatar: 'size-10', text: 'small' },
  lg: { avatar: 'size-12', text: 'base' },
} as const;

interface IAgentRoleAvatarProps {
  /** The team's key; picks the avatar's colour. */
  group: string;
  /** The role's title; its initials fill the avatar. */
  title: string;
  size?: keyof typeof SIZES;
  className?: string;
}

/**
 * A role drawn as its initials in its team's colour, so a team's people read
 * as a team at a glance. Deliberately not a face — an agent shouldn't pass for
 * a colleague in chats and approvals.
 */
export function AgentRoleAvatar({
  group,
  title,
  size = 'md',
  className,
}: IAgentRoleAvatarProps) {
  const sizes = SIZES[size];
  const accent = CATALOG_ACCENT[TEAM_ACCENT[group] ?? 'blue'];
  return (
    // Opaque base under the tinted fill, so it reads the same on any surface.
    <Avatar
      className={cn(sizes.avatar, 'shrink-0 bg-background', className)}
      aria-hidden="true"
      data-testid="agent-role-avatar"
    >
      <AvatarFallback className={accent.softTile}>
        <Text as="span" level={sizes.text} weight="medium">
          {getInitials(title)}
        </Text>
      </AvatarFallback>
    </Avatar>
  );
}

/** Roles shown in a team's cluster before the "+N". */
const CLUSTER_SIZE = 3;

/** Stagger for the hover hop, 50ms per avatar so it ripples left to right. */
const HOP_DELAY = ['delay-0', 'delay-[50ms]', 'delay-[100ms]', 'delay-[150ms]'];

/** "+N" takes the team's colour on hover. Literal classes, so Tailwind can see them. */
const HOVER_FILL: Record<TCatalogAccent, string> = {
  blue: 'group-hover/tile:bg-alternate-7/30',
  mint: 'group-hover/tile:bg-alternate-5/30',
  violet: 'group-hover/tile:bg-alternate-8/30',
  amber: 'group-hover/tile:bg-alternate-3/30',
  cyan: 'group-hover/tile:bg-alternate-6/30',
  rose: 'group-hover/tile:bg-alternate-9/30',
  olive: 'group-hover/tile:bg-alternate-4/30',
};

// The hop lives on a wrapper so its staggered delay doesn't hold back the colour fade.
const HOP = `inline-flex transform-gpu will-change-transform transition-transform duration-[350ms] motion-safe:group-hover/tile:-translate-y-[3px] motion-safe:group-hover/tile:scale-[1.08] ${SPRING_EASE_CLASS}`;

const DOT =
  'inline-flex size-7 items-center justify-center rounded-full transition-colors duration-[250ms]';

/** Slots in the 2×2 cluster; a small team leaves the rest empty so every tile matches. */
const CLUSTER_SLOTS = 4;

/**
 * A team as a 2×2 cluster on a soft tray: its first three roles' initials in
 * the team's colour, then "+N" for the rest. On a banner tile's hover the
 * avatars hop in a staggered wave and "+N" fills with the team's colour.
 */
export function AgentTeamAvatars({
  group,
  roles,
}: {
  /** The team's key; picks the colour. */
  group: string;
  /** The team's roles, in catalog order. */
  roles: readonly ITemplateCard[];
}) {
  const { t } = useTranslation('agent-builder');
  const accentKey = TEAM_ACCENT[group] ?? 'blue';
  const accent = CATALOG_ACCENT[accentKey];
  const shown = roles.slice(0, CLUSTER_SIZE);
  const more = roles.length - shown.length;
  return (
    <span
      className="grid shrink-0 grid-cols-2 gap-1 rounded-lg bg-muted p-1"
      aria-hidden="true"
      data-testid="agent-team-avatars"
    >
      {shown.map((role, index) => (
        <span key={role.id} className={cn(HOP, HOP_DELAY[index])}>
          <Text
            as="span"
            level="xSmall"
            weight="medium"
            className={cn(DOT, accent.softTile, 'text-foreground')}
          >
            {getInitials(role.title)}
          </Text>
        </span>
      ))}
      {more > 0 && (
        <span className={cn(HOP, HOP_DELAY[shown.length])}>
          <Text
            as="span"
            level="xSmall"
            weight="medium"
            color="muted"
            className={cn(
              DOT,
              'bg-foreground/10 tabular-nums group-hover/tile:text-foreground',
              HOVER_FILL[accentKey],
            )}
          >
            {t('agentBuilder.hiring.more', { count: more })}
          </Text>
        </span>
      )}
      {Array.from(
        { length: CLUSTER_SLOTS - shown.length - (more > 0 ? 1 : 0) },
        (_, index) => (
          <span
            key={`empty-${index}`}
            className="size-7 rounded-full border border-dashed border-border"
          />
        ),
      )}
    </span>
  );
}
